import { AccountBalance as AccountIcon } from '@mui/icons-material';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

// relative imports
import { currentAccountTaxYearVar } from '../../../../data/apollo/cache/reactiveVars';
import { isFeatureEnabled } from '../../../../data/app-config/configs/configUtilities';
import { useAccountConfig } from '../../../providers/AccountConfigProvider';
import { useAccount } from '../../../providers/AccountProvider';
import useAlternativeDisplay from '../../custom-hooks/useAlternativeDisplay';
import { upperCase } from '../../utilities/stringUtilities';

const useStyles = makeStyles()((theme, { isMobile }) => ({
  listItemRoot: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    outline: 'none',
    marginLeft: isMobile ? 0 : theme.spacing(3),
    paddingTop: 'inherit',
    paddingBottom: 'inherit',
    borderBottom: `${isMobile ? 1 : 0}px solid ${theme.palette.grey[200]}`,
  },
  leftBorder: {
    borderLeft: `${isMobile ? 0 : 1}px solid ${theme.palette.grey[200]}`,
  },
  accountButton: {
    maxWidth: 160,
    color: theme.whiteLabel.AccountBox.textColor || 'inherit',
  },
  greyText: {
    color: theme.palette.grey[isMobile ? 600 : 300],
    display: 'block',
  },
  textPadLeft: {
    color:
      theme.whiteLabel.HeaderText.color || isMobile
        ? undefined
        : theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    paddingLeft: '12px',
  },
  textPadLeftSecondary: {
    paddingLeft: '12px',
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.grey[isMobile ? 700 : 400],
    marginBottom: theme.spacing(isMobile ? 0.5 : 0),
  },
  acctButtonIcon: {
    minWidth: 'auto',
    width: 'auto',
    color: theme.whiteLabel.AccountBox.textColor || 'inherit',
  },
  noPad: {
    padding: 0,
  },
  link: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.grey[isMobile ? 600 : 400],
    display: 'block',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.grey[isMobile ? 700 : 500],
      textDecoration: 'underline',
    },
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
    marginBottom: theme.spacing(0.5),
  },
}));

const SimpleAccountDisplay = () => {
  const { isMobile } = useAlternativeDisplay();
  const { classes, cx } = useStyles({ isMobile });
  const { currentAccount, clientPermissions: permissions } = useAccount();
  const { accountConfig } = useAccountConfig();
  const { isAdminViewingClient } = permissions;

  const taxPayerDetails = currentAccount.taxPayerDetails || {};
  const currentPreparerLink = currentAccount?.preparerLink;
  const defaultTaxYear = currentAccountTaxYearVar();
  const manageTaxMethodEnabled = isFeatureEnabled({
    accountConfig,
    moduleName: 'tax',
    featureName: 'manageTaxMethod',
  });
  const manageTaxYearEnabled = isFeatureEnabled({
    accountConfig,
    moduleName: 'tax',
    featureName: 'manageTaxYear',
  });
  const accountTypeLabel = accountConfig?.display?.accountTypeLabel;

  return (
    <ListItem
      className={classes.leftBorder}
      classes={{
        root: classes.listItemRoot,
        gutters: classes.textPadLeft,
      }}
      alignItems="center"
    >
      <ListItemIcon
        classes={{
          root: classes.acctButtonIcon,
        }}
      >
        <AccountIcon className={classes.greyText} fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={
          isAdminViewingClient
            ? currentPreparerLink?.clientAccountName
            : currentAccount.name
        }
        primaryTypographyProps={{
          classes: {
            root: classes.textPadLeft,
            colorPrimary: classes.greyText,
          },
          variant: 'body1',
          noWrap: true,
        }}
        secondary={
          manageTaxYearEnabled
            ? `Tax Year ${taxPayerDetails.taxYear || defaultTaxYear}
          ${
            manageTaxMethodEnabled && taxPayerDetails.taxMethod
              ? ` (${upperCase(taxPayerDetails.taxMethod)})`
              : ''
          }`
            : accountTypeLabel
        }
        secondaryTypographyProps={{
          classes: {
            root: classes.textPadLeftSecondary,
            colorPrimary: classes.greyText,
          },
          variant: 'body2',
          noWrap: true,
        }}
        className={cx(classes.noPad, classes.accountButton)}
      />
    </ListItem>
  );
};

export default SimpleAccountDisplay;
